import React, { useEffect, useState, useRef } from "react";
import Header from "./Header";
import SideMenu from "./SideMenu";
import api from "../../api/axiosInstance";

import RightInApp from "../img/RightInApp.svg";
import { useNavigate } from "react-router-dom";
import AppFooter from "./AppFooter";
const AppList = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true); // first page loader
  const [loadingMore, setLoadingMore] = useState(false); // subsequent pages loader
  const [nextUrl, setNextUrl] = useState(null); // API next link
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Sentinel for infinite scrolling (viewport-based)
  const sentinelRef = useRef(null);
  const isFetchingRef = useRef(false); // prevent overlapping fetches
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Convert absolute `next` URL -> relative (works with axiosInstance baseURL)
  const toRelative = (url) => url?.replace(/^https?:\/\/[^/]+\//, "") || null;

  // Fetch a page: default is first page; pass nextUrl for subsequent pages
  const fetchPage = async (
    url = "/display/api/v1/list-project-for-display",
    { append = false } = {}
  ) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setError(null);
    append ? setLoadingMore(true) : setLoading(true);

    try {
      const res = await api.get(toRelative(url));
      const data = res?.data?.data;

      const results = data?.results ?? [];
      setApps((prev) => (append ? [...prev, ...results] : results));
      setNextUrl(toRelative(data?.next)); // null when no more pages
      setCount(data?.count ?? 0);
    } catch (err) {
      console.error("Error fetching apps:", err);
      setError("Failed to load apps.");
    } finally {
      isFetchingRef.current = false;
      append ? setLoadingMore(false) : setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && nextUrl && !loading && !loadingMore) {
          fetchPage(nextUrl, { append: true });
        }
      },
      {
        root: null, // viewport
        rootMargin: "200px", // start fetching a bit early
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextUrl, loading, loadingMore]);

  const AppDeta = (app) => {
    navigate("/AppDetails", {
      state: { id: app.id, project_type: app.project_type },
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      // Show button if scrolled down more than 300px
      setShowScrollTop(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <div className="w-12 h-12 border-4 border-[#7D5AE3] rounded-full border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Top Banner Section */}
      <div className="w-full h-24 bg-[#E7DEF9] flex flex-col justify-center items-center">
        <h1 className="text-2xl font-inter font-semibold text-center px-2">
          Everything you need to manage your business in one place.
        </h1>
      </div>

      {/* Main Content Section */}
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar Section */}
        <div className="w-full lg:w-2/12 h-auto ">
          <SideMenu /> {/* Use the SidebarMenu component */}
        </div>
        <div className="w-px bg-gray-300"></div>

        {/* Featured Apps Section */}

        <div className="w-full lg:w-10/12  px-4 sm:px-6 md:px-8 lg:px-12 ">
          <h1 className="font-inter mt-4 text-xl font-semibold">
            Featured Applications
          </h1>

          {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-5 mb-3">
            {apps.map((app, index) => (
              <div
                key={index}
                className="w-full h-[200px] rounded-md  p-4 flex gap-3 bg-white"
                style={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                {/* App Logo */}
                <div className="w-2/12 flex justify-center items-start">
                  <img
                    src={app.logo}
                    alt={app.name}
                    className="w-8 h-8 object-cover rounded-full"
                    onError={(e) =>
                      (e.currentTarget.style.visibility = "hidden")
                    }
                  />
                </div>

                {/* App Details */}
                <div
                  className="w-10/12 flex flex-col justify-between cursor-pointer"
                  onClick={() => AppDeta(app)}
                >
                  <div className="flex flex-col">
                    <h1 className="text-lg font-bold font-inter text-[#60448c] truncate">
                      {app.name}
                    </h1>
                    <p className="text-sm font-inter mt-2 text-gray-700 line-clamp-5 overflow-hidden">
                      {app.description}
                    </p>
                  </div>

                  {/* Try Now Link */}
                  <div className="mt-2">
                    <span
                      className="flex gap-2 text-sm cursor-pointer font-semibold"
                      style={{ color: "#9367D7" }}
                      onClick={() => AppDeta(app)}
                    >
                      Try Now <img src={RightInApp} alt="Arrow" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom mini loader */}
          {loadingMore && (
            <div className="py-3 text-center text-sm text-gray-600">
              Loading more…
            </div>
          )}

          {/* Invisible sentinel that triggers loading of next page */}
          <div ref={sentinelRef} style={{ height: 1 }} />

          {/* Optional: end-of-list indicator */}
          {/* {!nextUrl && apps.length >= count && (
            <div className="py-2 text-center text-xs text-gray-500">
              All items loaded.
            </div>
          )} */}
        </div>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-[#7D5AE3] text-white p-3 rounded-full shadow-lg hover:bg-[#60448c] transition-colors"
          title="Scroll to top"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
      <AppFooter></AppFooter>
    </div>
  );
};

export default AppList;
